import { Linkedin, Star } from "lucide-react";
import { ScrambleText } from "../components/ScrambleText";

const projects = [
  {
    title: "deep-threat-model",
    description: "Uses multi-agents to craft comprehensive and reliable threat models",
    url: "https://github.com/d0gesec/deep-threat-model",
    stars: 13,
    mentions: [
      {
        label: "Mentioned by Shostack",
        url: "https://shostack.org/blog/appsec-roundup-march-2025/",
      },
    ],
  },
  {
    title: "mcp-kali",
    description: "MCP server built for real-world offensive security and CTF work",
    url: "https://github.com/d0gesec/mcp-kali",
    stars: 0,
  },
];

const writeups = [
  {
    title: "Threat hunting exercise: Privilege Escalation by Setuid and Setguid",
    url: "https://medium.com/p/fa7457540668",
    platform: "Medium",
    views: "3k",
  },
  {
    title: "Exploring Audit Daemon for Threat Hunting",
    url: "https://medium.com/p/6ef2a30570ad",
    platform: "Medium",
    views: "3.8k",
  },
];

export function Home() {
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight">
        <ScrambleText texts={["Pak H.", "Tony"]} interval={20000} /> Lee
      </h1>
      <p className="mt-2 text-muted-foreground leading-relaxed">
        Security engineering by day, building tech projects by night. I'm a security engineer who loves solving software engineering problems too, whether it's making tools, breaking things, or just building whatever sounds fun.
      </p>
      <div className="mt-4 gap-4 flex">
        <a
          href="https://www.linkedin.com/in/pak-h-lee/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Linkedin className="h-4 w-4" />
        </a>
        <a
          href="https://x.com/d0gesec"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        </a>
      </div>

      <section className="mt-12">
        <h2 className="text-lg font-semibold tracking-tight">Recent Activity</h2>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <a
            href="https://github.com/d0gesec/mcp-kali"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg border p-4 hover:bg-accent transition-colors"
          >
            <p className="text-sm font-medium">MCP Kali</p>
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
              Released MCP server built for real-world offensive security and CTF work.
            </p>
          </a>
          <div className="rounded-lg border p-4">
            <p className="text-sm font-medium">Agentic Harness for Offsec</p>
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
              Curated collections of agents, skills, hooks, and MCPs for autonomous offensive security.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">Coming soon</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-sm font-bold">Hack The Box (Hall of Fame)</p>
            <img
              src="/ranking.png"
              alt="Hack The Box Ranking"
              className="mt-3 rounded"
            />
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-lg font-semibold tracking-tight">Open Source</h2>
        <div className="mt-2 space-y-0">
          {projects.map((project) => (
            <div key={project.url}>
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-baseline justify-between gap-4 py-1 group"
              >
                <div>
                  <span className="text-sm font-medium group-hover:underline">{project.title}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{project.description}</span>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                  <Star className="h-3 w-3" />
                  {project.stars}
                </span>
              </a>
              {project.mentions?.map((mention) => (
                <a
                  key={mention.url}
                  href={mention.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-4 flex items-center gap-1 py-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span className="text-muted-foreground/50">└</span>
                  {mention.label}
                </a>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-lg font-semibold tracking-tight">Writeups</h2>
        <div className="mt-2 space-y-0">
          {writeups.map((post) => (
            <a
              key={post.url}
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-baseline justify-between gap-4 py-1 group"
            >
              <span className="text-sm font-medium group-hover:underline">{post.title}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {post.platform} · {post.views}
              </span>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
